// Studio IA Local — Landing page & liste d'attente
// Un logiciel Windows open source 100% gratuit pour installer un environnement IA complet
// (Ollama, ComfyUI, Stable Diffusion, n8n, Flowise…).

import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Cpu, HardDrive, Zap, Package, Terminal, Wand2, Bot, Video, Mic,
  Sparkles, Download, Check, Lock, Github, Loader2, Shield, Palette, Rocket,
  ChevronRight, Mail, Copy, Share2, Trophy, Users,
} from "lucide-react";

const PROFILS = [
  {
    key: "light",
    name: "Installation Légère",
    tag: "Débutant · 8 Go RAM",
    color: "from-emerald-400/30 to-teal-400/10 border-emerald-400/40",
    icon: <Zap size={22} className="text-emerald-300" />,
    tools: [
      { name: "Ollama",     desc: "Moteur de LLM local" },
      { name: "Open WebUI", desc: "Interface web pour chatter" },
      { name: "Git",        desc: "Outil de versioning" },
      { name: "Python 3.11", desc: "Runtime nécessaire" },
      { name: "Qwen / Gemma", desc: "Modèle IA léger auto-téléchargé" },
    ],
  },
  {
    key: "creator",
    name: "Installation Créateur",
    tag: "Recommandé · 16 Go RAM + GPU NVIDIA",
    color: "from-fuchsia-500/30 to-purple-500/10 border-fuchsia-400/40",
    icon: <Palette size={22} className="text-fuchsia-300" />,
    tools: [
      { name: "Tout de la version légère", desc: "" },
      { name: "ComfyUI", desc: "Générateur d'images node-based" },
      { name: "FLUX", desc: "Modèle de génération d'images de pointe" },
      { name: "Stable Diffusion XL", desc: "SDXL + modèles associés" },
      { name: "Extensions & LoRAs", desc: "Prêts à charger" },
    ],
  },
  {
    key: "full",
    name: "Installation Complète",
    tag: "Studio Pro · 32 Go RAM + GPU 12 Go VRAM",
    color: "from-amber-400/30 to-orange-500/10 border-amber-400/40",
    icon: <Rocket size={22} className="text-amber-300" />,
    tools: [
      { name: "Tout de la version Créateur", desc: "" },
      { name: "n8n", desc: "Automatisations & workflows" },
      { name: "Flowise", desc: "Constructeur d'agents IA visuels" },
      { name: "Supabase (local)", desc: "Base de données open source" },
      { name: "Génération vidéo open source", desc: "Compatible avec ta machine" },
    ],
  },
];

const FEATURES = [
  { icon: <Shield size={18} />,   title: "100% Gratuit",          text: "Aucun abonnement, aucune publicité, aucune licence payante imposée." },
  { icon: <Github size={18} />,   title: "Open Source",           text: "Code source complet, MIT — audite, modifie, partage." },
  { icon: <Cpu size={18} />,      title: "Détection auto",        text: "Vérifie GPU NVIDIA, RAM, espace disque, Ollama pré-installé." },
  { icon: <Package size={18} />,  title: "Gestion des modèles",   text: "Ajoute, supprime, met à jour tes modèles Ollama en 1 clic." },
  { icon: <Download size={18} />, title: "Toujours à jour",       text: "Téléchargements depuis les dépôts officiels · dernières versions." },
  { icon: <Palette size={18} />,  title: "Interface moderne",     text: "Thème clair/sombre, barre de progression, journal d'installation." },
];

const MODULES_FUTURS = [
  { icon: <Wand2 size={16} />, label: "Génération d'images" },
  { icon: <Video size={16} />, label: "Génération de vidéos" },
  { icon: <Mic size={16} />,   label: "Clonage de voix" },
  { icon: <Bot size={16} />,   label: "Assistants IA spécialisés" },
  { icon: <Terminal size={16} />, label: "Outils de développement" },
  { icon: <Zap size={16} />,   label: "Automatisations" },
];

const TECH_STACK = [
  { name: "Python",         icon: "🐍" },
  { name: "CustomTkinter",  icon: "🖼️" },
  { name: "PyInstaller",    icon: "📦" },
  { name: "Inno Setup",     icon: "⚙️" },
  { name: "Windows 10/11",  icon: "🪟" },
];

export default function StudioIALocalPage() {
  const [params] = useSearchParams();
  const referrerCode = (params.get("ref") || "").toLowerCase().trim();

  const [email, setEmail] = useState("");
  const [profile, setProfile] = useState("creator");
  const [busy, setBusy] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(null);
  // Status personnel après inscription (rang + parrainages + code)
  const [status, setStatus] = useState(null);

  // Compteur inscrits (rafraîchi après chaque inscription)
  const refreshCount = () => {
    api.get("/studio-ia-local/waitlist/count")
      .then(({ data }) => setWaitlistCount(data.count || 0))
      .catch(() => {});
  };
  useEffect(() => { refreshCount(); }, []);

  // Restaure le status si l'utilisateur a déjà son ref_code en localStorage
  useEffect(() => {
    const saved = localStorage.getItem("studio_ia_ref_code");
    if (saved) {
      api.get(`/studio-ia-local/waitlist/status/${saved}`)
        .then(({ data }) => {
          if (data.found) {
            setStatus(data);
            setSubscribed(true);
          }
        }).catch(() => {});
    }
  }, []);

  const subscribe = async (e) => {
    e?.preventDefault();
    if (!email.match(/^[^@\s]+@[^@\s]+\.[^@\s]+$/)) {
      toast.error("Email invalide");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/studio-ia-local/waitlist", {
        email,
        profile,
        ref: referrerCode || undefined,
      });
      setSubscribed(true);
      if (data.ref_code) {
        localStorage.setItem("studio_ia_ref_code", data.ref_code);
        setStatus(data);
      }
      refreshCount();
      toast.success(data.already ? "Tu es déjà sur la liste ✓" : "Inscription enregistrée · on te tient au courant 🚀");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.response?.data?.error || "Erreur";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const shareUrl = status?.ref_code
    ? `https://www.voamada.com/studio-ia-local?ref=${status.ref_code}`
    : "";
  const shareMsg = `🧬 Studio IA Local — un logiciel gratuit qui installe un environnement IA complet sur Windows en 1 clic. J'attends la sortie, rejoins-moi :`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Lien copié 📋");
    } catch { toast.error("Impossible de copier"); }
  };
  const shareTo = (kind) => {
    const enc = encodeURIComponent;
    const urls = {
      whatsapp: `https://wa.me/?text=${enc(shareMsg + " " + shareUrl)}`,
      twitter:  `https://twitter.com/intent/tweet?text=${enc(shareMsg)}&url=${enc(shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`,
      telegram: `https://t.me/share/url?url=${enc(shareUrl)}&text=${enc(shareMsg)}`,
    };
    if (navigator.share && kind === "native") {
      navigator.share({ title: "Studio IA Local", text: shareMsg, url: shareUrl }).catch(() => {});
    } else if (urls[kind]) {
      window.open(urls[kind], "_blank", "noopener,width=600,height=500");
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="studio-ia-local-page">
      {/* HERO */}
      <header className="relative overflow-hidden border-b border-fuchsia-400/20">
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(1000px 400px at 30% 0%, rgba(217,70,239,0.35), transparent 70%), radial-gradient(800px 300px at 80% 20%, rgba(59,130,246,0.25), transparent 70%)" }} />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60'%3E%3Cpath d='M0 0h60v60H0z' fill='none'/%3E%3Cpath d='M0 30h60M30 0v60' stroke='%23fff' stroke-width='0.5'/%3E%3C/svg%3E\")" }} />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-fuchsia-500/15 border border-fuchsia-400/40 text-fuchsia-200 text-[10px] uppercase tracking-widest font-bold mb-6">
            <Sparkles size={12} /> Bientôt disponible · Version 1.0
          </div>

          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.95] max-w-4xl">
            <span className="bg-gradient-to-br from-fuchsia-300 via-purple-300 to-blue-300 bg-clip-text text-transparent">
              Studio IA Local
            </span>
          </h1>
          <p className="mt-4 text-lg sm:text-2xl text-white/80 max-w-3xl leading-snug">
            Ton environnement d'intelligence artificielle complet, installé en <strong className="text-white">un clic</strong> sur Windows.<br className="hidden sm:block" />
            <span className="text-fuchsia-200 font-semibold">100% gratuit, 100% open source, 100% local.</span>
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            {[
              { icon: <Lock size={13} />, label: "Aucun abonnement" },
              { icon: <Shield size={13} />, label: "Données 100% locales" },
              { icon: <Github size={13} />, label: "MIT License" },
              { icon: <Zap size={13} />,   label: "Installation 1-clic" },
            ].map(b => (
              <span key={b.label} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/10 text-xs text-white/80">
                {b.icon} {b.label}
              </span>
            ))}
          </div>

          {/* Compteur inscrits — preuve sociale */}
          {waitlistCount !== null && waitlistCount > 0 && (
            <div className="mt-6 inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-fuchsia-500/15 border border-fuchsia-400/30 text-sm" data-testid="studio-waitlist-count">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-fuchsia-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-fuchsia-400" />
              </span>
              <span className="font-bold text-fuchsia-100">
                <span className="text-white text-lg tabular-nums">{waitlistCount.toLocaleString('fr-FR')}</span>{" "}
                <span className="text-fuchsia-200">développeur{waitlistCount > 1 ? "s" : ""} sur la liste d&apos;attente</span>
              </span>
            </div>
          )}

          {/* CTA Waitlist */}
          <div className="mt-10 max-w-2xl">
            {subscribed && status ? (
              <div className="rounded-2xl border border-emerald-400/40 bg-emerald-500/10 p-5 space-y-4" data-testid="studio-waitlist-success">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 shrink-0 rounded-full bg-emerald-400 flex items-center justify-center">
                    <Check size={20} className="text-black" strokeWidth={3} />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-emerald-200 text-base">Tu es sur la liste ! 🎉</p>
                    <p className="text-sm text-white/70 mt-0.5">Chaque parrainage te fait gagner 5 places dans la file.</p>
                  </div>
                </div>

                {/* Stats rang + parrainages */}
                <div className="grid grid-cols-3 gap-2 pt-2 border-t border-white/10">
                  <div className="text-center">
                    <p className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Rang</p>
                    <p className="text-2xl font-black text-emerald-200 tabular-nums" data-testid="studio-rank">
                      #{status.priority_rank}
                    </p>
                    {status.priority_rank !== status.raw_rank && (
                      <p className="text-[9px] text-white/40 line-through">#{status.raw_rank}</p>
                    )}
                  </div>
                  <div className="text-center border-l border-r border-white/10">
                    <p className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Parrainages</p>
                    <p className="text-2xl font-black text-fuchsia-300 tabular-nums" data-testid="studio-referrals">
                      {status.referrals || 0}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Total</p>
                    <p className="text-2xl font-black text-white tabular-nums">
                      {status.total || waitlistCount || 0}
                    </p>
                  </div>
                </div>

                {/* Lien de parrainage + boutons de partage */}
                <div className="pt-3 border-t border-white/10 space-y-2">
                  <p className="text-xs text-white/70 font-bold uppercase tracking-widest flex items-center gap-1.5">
                    <Trophy size={12} className="text-amber-300" /> Ton lien magique
                  </p>
                  <div className="flex gap-2">
                    <input
                      readOnly
                      value={shareUrl}
                      onFocus={(e) => e.target.select()}
                      data-testid="studio-share-url"
                      className="flex-1 rounded-xl bg-black/50 border border-white/15 px-3 py-2 text-xs text-emerald-200 font-mono truncate"
                    />
                    <button
                      onClick={copyLink}
                      data-testid="studio-share-copy"
                      className="shrink-0 px-3 py-2 rounded-xl bg-emerald-500/20 border border-emerald-400/40 hover:bg-emerald-500/30 text-emerald-200 text-xs font-bold inline-flex items-center gap-1"
                    >
                      <Copy size={12} /> Copier
                    </button>
                  </div>
                  <div className="flex gap-1.5 pt-1 flex-wrap">
                    <button onClick={() => shareTo("whatsapp")} data-testid="studio-share-wa" className="flex-1 min-w-[80px] px-3 py-2 rounded-xl bg-[#25D366]/15 border border-[#25D366]/40 hover:bg-[#25D366]/25 text-[#25D366] text-xs font-bold inline-flex items-center justify-center gap-1">
                      💬 WhatsApp
                    </button>
                    <button onClick={() => shareTo("twitter")} data-testid="studio-share-tw" className="flex-1 min-w-[80px] px-3 py-2 rounded-xl bg-white/10 border border-white/25 hover:bg-white/20 text-white text-xs font-bold inline-flex items-center justify-center gap-1">
                      𝕏 Twitter
                    </button>
                    <button onClick={() => shareTo("facebook")} data-testid="studio-share-fb" className="flex-1 min-w-[80px] px-3 py-2 rounded-xl bg-[#1877F2]/15 border border-[#1877F2]/40 hover:bg-[#1877F2]/25 text-[#4F9BFF] text-xs font-bold inline-flex items-center justify-center gap-1">
                      f Facebook
                    </button>
                    <button onClick={() => shareTo("telegram")} data-testid="studio-share-tg" className="flex-1 min-w-[80px] px-3 py-2 rounded-xl bg-[#0088cc]/15 border border-[#0088cc]/40 hover:bg-[#0088cc]/25 text-[#5AB8E7] text-xs font-bold inline-flex items-center justify-center gap-1">
                      ✈ Telegram
                    </button>
                  </div>
                  <p className="text-[10px] text-white/40 italic pt-1">
                    Partage ton lien : chaque inscription via toi = 5 places gagnées dans la file. Passe champion 🏆
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={subscribe} data-testid="studio-waitlist-form" className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur p-4 sm:p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-fuchsia-200 font-bold uppercase tracking-widest">
                    <Mail size={12} /> Rejoins la liste d'attente
                  </div>
                  {referrerCode && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-400/15 border border-amber-400/40 text-[10px] font-bold text-amber-200" data-testid="studio-ref-tag">
                      <Users size={10} /> Invité par un ami
                    </span>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ton.email@exemple.com"
                    data-testid="studio-waitlist-email"
                    className="flex-1 rounded-xl bg-black/50 border border-white/15 px-4 py-3 text-sm placeholder:text-white/30 focus:border-fuchsia-400/60 focus:outline-none"
                  />
                  <button
                    type="submit"
                    disabled={busy}
                    data-testid="studio-waitlist-submit"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-fuchsia-500 to-purple-500 hover:brightness-110 text-white font-bold text-sm disabled:opacity-50 whitespace-nowrap"
                  >
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} M'inscrire
                  </button>
                </div>
                <p className="text-[10px] text-white/40">
                  Un email au lancement · Pas de spam · Désinscription en 1 clic.
                  {referrerCode && <span className="text-amber-300"> · Tu bénéficies d'une priorité 🎁</span>}
                </p>
              </form>
            )}
          </div>
        </div>
      </header>

      {/* PROFILS D'INSTALLATION */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16" id="profils">
        <div className="text-center mb-10">
          <p className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-300 font-bold mb-2">3 profils, 1 clic</p>
          <h2 className="text-3xl sm:text-4xl font-black">Choisis ta configuration</h2>
          <p className="mt-3 text-white/60 max-w-2xl mx-auto">
            L'installateur détecte automatiquement les capacités de ton PC et t'aide à choisir le profil adapté.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {PROFILS.map((p) => {
            const selected = profile === p.key;
            return (
              <button
                key={p.key}
                onClick={() => setProfile(p.key)}
                data-testid={`studio-profil-${p.key}`}
                className={`text-left rounded-2xl border p-5 transition bg-gradient-to-br ${p.color} ${selected ? "ring-2 ring-white/30 scale-[1.01]" : "opacity-85 hover:opacity-100"}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  {p.icon}
                  {selected && <span className="ml-auto inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-white/90 bg-white/15 rounded-full px-2 py-0.5"><Check size={10} /> Choisi</span>}
                </div>
                <h3 className="font-black text-lg leading-tight">{p.name}</h3>
                <p className="text-[10px] uppercase tracking-widest text-white/60 mt-1">{p.tag}</p>
                <ul className="mt-4 space-y-1.5 text-sm">
                  {p.tools.map((t, i) => (
                    <li key={i} className="flex items-start gap-2 text-white/85">
                      <ChevronRight size={14} className="mt-0.5 shrink-0 text-white/40" />
                      <div>
                        <span className="font-bold">{t.name}</span>
                        {t.desc && <span className="block text-[11px] text-white/50">{t.desc}</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>
      </section>

      {/* FEATURES */}
      <section className="border-y border-white/10 bg-white/[0.02]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
          <div className="text-center mb-10">
            <p className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-300 font-bold mb-2">Fonctionnalités clés</p>
            <h2 className="text-3xl sm:text-4xl font-black">Un vrai truc de Pro</h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-black/40 p-5 hover:border-fuchsia-400/40 hover:-translate-y-0.5 transition">
                <div className="w-10 h-10 rounded-xl bg-fuchsia-500/15 border border-fuchsia-400/30 flex items-center justify-center text-fuchsia-200 mb-3">
                  {f.icon}
                </div>
                <h3 className="font-bold text-base">{f.title}</h3>
                <p className="text-sm text-white/60 mt-1 leading-snug">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STACK TECH + MODULES FUTURS */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-2 gap-8">
        <div className="rounded-3xl border border-white/10 bg-black/40 p-6 sm:p-8">
          <p className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-300 font-bold mb-2">Sous le capot</p>
          <h2 className="text-2xl sm:text-3xl font-black mb-4">Architecture modulaire</h2>
          <p className="text-white/70 text-sm mb-5">
            Compilé avec PyInstaller, packagé en <code className="text-fuchsia-200 bg-fuchsia-500/10 px-1 rounded">.exe</code> Windows via Inno Setup.
            Le code est découpé en modules :
          </p>
          <ul className="text-sm space-y-2">
            {[
              "Interface utilisateur (UI)",
              "Vérifications matériel (CPU, RAM, GPU, disque)",
              "Téléchargement (dépôts officiels uniquement)",
              "Installation & configuration",
              "Gestion des modèles IA",
              "Mises à jour automatiques",
            ].map((m, i) => (
              <li key={i} className="flex items-start gap-2 text-white/85">
                <Check size={14} className="mt-1 text-fuchsia-300 shrink-0" />
                <span>{m}</span>
              </li>
            ))}
          </ul>
          <div className="mt-5 flex flex-wrap gap-2">
            {TECH_STACK.map((s) => (
              <span key={s.name} className="text-xs px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/10 text-white/75 font-bold">
                {s.icon} {s.name}
              </span>
            ))}
          </div>
        </div>

        <div className="rounded-3xl border border-fuchsia-400/25 bg-gradient-to-br from-fuchsia-500/10 to-purple-500/5 p-6 sm:p-8">
          <p className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-300 font-bold mb-2">Roadmap</p>
          <h2 className="text-2xl sm:text-3xl font-black mb-4">Modules à venir</h2>
          <p className="text-white/70 text-sm mb-5">
            Architecture pensée pour l'extensibilité — chaque module s'ajoute sans casser l'existant :
          </p>
          <div className="grid grid-cols-2 gap-2">
            {MODULES_FUTURS.map((m, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white/[0.05] border border-white/10 text-sm">
                <span className="text-fuchsia-300">{m.icon}</span>
                <span className="font-bold">{m.label}</span>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-5 border-t border-white/10 flex items-center gap-2 text-xs text-white/60">
            <Sparkles size={14} className="text-amber-300" />
            <span>Les APIs externes sont <strong className="text-white">toujours optionnelles</strong> — ton studio reste 100% fonctionnel offline.</span>
          </div>
        </div>
      </section>

      {/* CAHIER DES CHARGES */}
      <section className="border-y border-white/10 bg-white/[0.02]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
          <div className="text-center mb-10">
            <p className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-300 font-bold mb-2">Transparence totale</p>
            <h2 className="text-3xl sm:text-4xl font-black">Cahier des charges</h2>
            <p className="mt-3 text-white/60 max-w-2xl mx-auto">
              Le projet est piloté par un cahier des charges public. Consulte-le, propose des améliorations.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto text-sm">
            {[
              { k: "🎯 Objectif",       v: "Installateur Windows .exe → environnement IA open source complet, sans abonnement obligatoire." },
              { k: "🛠️ Technologies",   v: "Python + CustomTkinter / PySide6, compilation PyInstaller, installateur Inno Setup / NSIS." },
              { k: "💻 Compatibilité",  v: "Windows 11 prioritaire, Windows 10 si possible." },
              { k: "🚀 Livrables",      v: "Code source + .exe compilé + script installateur + documentation de build." },
              { k: "🔒 Gratuité",       v: "Aucune pub, aucun abonnement, aucune licence payante imposée. MIT License." },
              { k: "🔄 Mises à jour",   v: "Bouton 'Rechercher les mises à jour' → vérifie versions et composants installés." },
              { k: "📦 Téléchargements",v: "Toujours depuis les dépôts officiels (Ollama, GitHub, Hugging Face)." },
              { k: "🧩 Extensibilité",  v: "Architecture modulaire — nouveaux modules IA branchables sans refactor." },
            ].map((it, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-black/30 p-4">
                <p className="text-xs font-bold text-fuchsia-200 mb-1">{it.k}</p>
                <p className="text-white/75">{it.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FOOTER */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-fuchsia-500/15 border border-fuchsia-400/40 text-fuchsia-200 text-[10px] uppercase tracking-widest font-bold mb-4">
          Prêt pour la révolution IA locale ?
        </div>
        <h2 className="text-3xl sm:text-5xl font-black mb-4">Reprends le contrôle de ton IA.</h2>
        <p className="text-white/70 max-w-xl mx-auto mb-8">
          Zero abonnement mensuel. Zero limite d'usage. Zero envoi de données à OpenAI/Google.
          Juste toi, ton PC, et un studio IA open source complet.
        </p>
        {!subscribed && (
          <a href="#profils" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-fuchsia-500 to-purple-500 hover:brightness-110 text-white font-bold text-sm">
            <Sparkles size={16} /> Choisir mon profil et m'inscrire
          </a>
        )}
        <div className="mt-8 text-xs text-white/40">
          Un projet <Link to="/" className="text-fuchsia-300 hover:underline">Voamada</Link> · <span className="text-fuchsia-300">MIT License</span> · Fait à Madagascar 🇲🇬
        </div>
      </section>
    </div>
  );
}
